import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LocalShipping,
  Phone,
  Email,
  Star,
  TrendingUp,
  LocalOffer,
  NewReleases,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import useUserStore from '../stores/userStore';
import promotionalService from '../services/promotionalService';
import HeroVideo from '../components/unsecured/HeroVideo';
import HeroAdCarousal from '../components/unsecured/HeroAdCarousal';
import CategoryCarousel from '../components/unsecured/CategoryCarousel';
import DiscountRangeCarousel from '../components/unsecured/DiscountRangeCarousel';
import CategoryGrid from '../components/unsecured/CategoryGrid';
import ShopByHealthConcern from '../components/unsecured/ShopByHealthConcern';

const HomeView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [discountProducts, setDiscountProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);

  useEffect(() => {
    const loadHomeData = async () => {
      try {
        setLoading(true);
        
        // Load all promotional content in parallel
        const [
          trendingResponse,
          discountResponse,
          bestSellersResponse,
          newArrivalsResponse,
        ] = await Promise.all([
          promotionalService.getTrendingProducts({ page: 1 }),
          promotionalService.getDiscountProducts({ page: 1 }),
          promotionalService.getBestSellers({ page: 1 }),
          promotionalService.getNewArrivals({ page: 1 }),
        ]);

        // Transform API response to match expected format
        const trendingMapped = trendingResponse.data.results?.map(item => {
          const images = item.product_data?.images || item.otc_data?.images;
          let firstImage;
          
          // Handle both array and string formats
          if (Array.isArray(images)) {
            // If images is an array, get the first URL directly
            firstImage = images[0];
          } else if (images) {
            // If images is a string, split and get the first image name
            firstImage = images.split(',')[0];
          }
          
          // Use different image paths based on product type and data format
          let imagePath = '/api/placeholder/200/200';
          if (firstImage) {
            if (Array.isArray(images)) {
              // If images is an array, use the URL directly
              imagePath = firstImage;
            } else {
              // If images is a string, construct path to local assets
              imagePath = item.product_data ? `/assets/Drugs Images/${firstImage}` : `/assets/images_1mg_otc/${firstImage}`;
            }
          }
          
          console.log('Image processing:', {
            originalImages: images,
            firstImage: firstImage,
            imagePath: imagePath
          });
          
          return {
            id: item.product_data?.id || item.otc_data?.id, // Use actual product/OTC ID
            promotionalId: item.id, // Keep promotional ID for reference
            name: item.product_data?.name || item.otc_data?.name,
            manufacturer: item.product_data?.manufacturer || item.otc_data?.manufacturer,
            salt_composition: item.product_data?.salt_composition || item.otc_data?.pack_info,
            price: item.product_data?.price || item.otc_data?.price,
            discount_percent: item.product_data?.discount_percent || item.otc_data?.discount_percent,
            image: imagePath,
            prescription_required: item.product_data?.prescription_required || item.otc_data?.prescription_required,
            category: item.otc_data?.category,
            type: item.item_type
          };
        }) || [];
        
        console.log('Trending products mapped:', trendingMapped);
        setTrendingProducts(trendingMapped);
        
        const discountMapped = discountResponse.data.results?.map(item => {
          const images = item.product_data?.images || item.otc_data?.images;
          let firstImage;
          
          // Handle both array and string formats
          if (Array.isArray(images)) {
            // If images is an array, get the first URL directly
            firstImage = images[0];
          } else if (images) {
            // If images is a string, split and get the first image name
            firstImage = images.split(',')[0];
          }
          
          // Use different image paths based on product type and data format
          let imagePath = '/api/placeholder/200/200';
          if (firstImage) {
            if (Array.isArray(images)) {
              // If images is an array, use the URL directly
              imagePath = firstImage;
            } else {
              // If images is a string, construct path to local assets
              imagePath = item.product_data ? `/assets/Drugs Images/${firstImage}` : `/assets/images_1mg_otc/${firstImage}`;
            }
          }
          
          return {
            id: item.product_data?.id || item.otc_data?.id, // Use actual product/OTC ID
            promotionalId: item.id, // Keep promotional ID for reference
            name: item.product_data?.name || item.otc_data?.name,
            manufacturer: item.product_data?.manufacturer || item.otc_data?.manufacturer,
            salt_composition: item.product_data?.salt_composition || item.otc_data?.pack_info,
            price: item.product_data?.price || item.otc_data?.price,
            discount_percent: item.product_data?.discount_percent || item.otc_data?.discount_percent,
            image: imagePath,
            prescription_required: item.product_data?.prescription_required || item.otc_data?.prescription_required,
            category: item.otc_data?.category,
            type: item.item_type
          };
        }) || [];
        
        console.log('Discount products mapped:', discountMapped);
        setDiscountProducts(discountMapped);
        
        const bestSellersMapped = bestSellersResponse.data.results?.map(item => {
          const images = item.product_data?.images || item.otc_data?.images;
          let firstImage;
          
          // Handle both array and string formats
          if (Array.isArray(images)) {
            // If images is an array, get the first URL directly
            firstImage = images[0];
          } else if (images) {
            // If images is a string, split and get the first image name
            firstImage = images.split(',')[0];
          }
          
          // Use different image paths based on product type and data format
          let imagePath = '/api/placeholder/200/200';
          if (firstImage) {
            if (Array.isArray(images)) {
              // If images is an array, use the URL directly
              imagePath = firstImage;
            } else {
              // If images is a string, construct path to local assets
              imagePath = item.product_data ? `/assets/Drugs Images/${firstImage}` : `/assets/images_1mg_otc/${firstImage}`;
            }
          }
          
          return {
            id: item.product_data?.id || item.otc_data?.id, // Use actual product/OTC ID
            promotionalId: item.id, // Keep promotional ID for reference
            name: item.product_data?.name || item.otc_data?.name,
            manufacturer: item.product_data?.manufacturer || item.otc_data?.manufacturer,
            salt_composition: item.product_data?.salt_composition || item.otc_data?.pack_info,
            price: item.product_data?.price || item.otc_data?.price,
            discount_percent: item.product_data?.discount_percent || item.otc_data?.discount_percent,
            image: imagePath,
            prescription_required: item.product_data?.prescription_required || item.otc_data?.prescription_required,
            category: item.otc_data?.category,
            type: item.item_type
          };
        }) || [];
        
        console.log('Best sellers response:', bestSellersResponse.data);
        console.log('Best sellers mapped:', bestSellersMapped);
        setBestSellers(bestSellersMapped);
        
        const newArrivalsMapped = newArrivalsResponse.data.results?.map(item => {
          const images = item.product_data?.images || item.otc_data?.images;
          let firstImage;
          
          // Handle both array and string formats
          if (Array.isArray(images)) {
            // If images is an array, get the first URL directly
            firstImage = images[0];
          } else if (images) {
            // If images is a string, split and get the first image name
            firstImage = images.split(',')[0];
          }
          
          // Use different image paths based on product type and data format
          let imagePath = '/api/placeholder/200/200';
          if (firstImage) {
            if (Array.isArray(images)) {
              // If images is an array, use the URL directly
              imagePath = firstImage;
            } else {
              // If images is a string, construct path to local assets
              imagePath = item.product_data ? `/assets/Drugs Images/${firstImage}` : `/assets/images_1mg_otc/${firstImage}`;
            }
          }
          
          return {
            id: item.product_data?.id || item.otc_data?.id, // Use actual product/OTC ID
            promotionalId: item.id, // Keep promotional ID for reference
            name: item.product_data?.name || item.otc_data?.name,
            manufacturer: item.product_data?.manufacturer || item.otc_data?.manufacturer,
            salt_composition: item.product_data?.salt_composition || item.otc_data?.pack_info,
            price: item.product_data?.price || item.otc_data?.price,
            discount_percent: item.product_data?.discount_percent || item.otc_data?.discount_percent,
            image: imagePath,
            prescription_required: item.product_data?.prescription_required || item.otc_data?.prescription_required,
            category: item.otc_data?.category,
            type: item.item_type
          };
        }) || [];
        
        console.log('New arrivals mapped:', newArrivalsMapped);
        setNewArrivals(newArrivalsMapped);
        
      } catch (err) {
        console.error('Error loading home data:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        // Don't set error, just show empty state
        setTrendingProducts([]);
        setDiscountProducts([]);
        setBestSellers([]);
        setNewArrivals([]);
      } finally {
        setLoading(false);
      }
    };

    loadHomeData();
  }, []);

  const ProductCard = ({ product }) => {
    const handleCardClick = () => {
      if (!product.id) {
        console.warn('Product ID is missing for:', product.name);
        return;
      }
      if (product.type === 'otc' || product.category) {
        window.location.href = `/otc/${product.id}`;
      } else {
        window.location.href = `/product/${product.id}`;
      }
    };

    return (
      <Card 
        onClick={handleCardClick}
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          width: '240px',
          height: '100%',
          boxSizing: 'border-box',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            borderColor: 'rgba(0, 0, 0, 0.12)'
          }
        }}
      >
      {/* Product Image Section */}
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        height: '140px',
        flexShrink: 0,
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <CardMedia
          component="img"
          image={product.image || '/api/placeholder/200/200'}
         
          sx={{ 
            objectFit: 'cover',
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto'
          }}
        />
      </Box>
      <CardContent sx={{ 
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        padding: { xs: '8px', sm: '10px', md: '12px' },
        minHeight: 0,
        boxSizing: 'border-box',
        '&:last-child': { pb: { xs: '8px', sm: '10px', md: '12px' } }
      }}>
        {/* Product Name - Fixed 2 lines with ellipsis */}
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.3,
            mb: 0.25,
            letterSpacing: '-0.01em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            height: '2.6em',
            flexShrink: 0
          }}
        >
          {product.name}
        </Typography>
        
        {/* Manufacturer - Uppercase and darker */}
        {product.manufacturer && (
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.7rem',
              color: '#395093', // primary.dark color directly
              fontWeight: 900,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: 0.65,
              lineHeight: 1.4,
              textTransform: 'uppercase',
              mb: 0.5,
              flexShrink: 0
            }}
          >
            {product.manufacturer?.toUpperCase()}
          </Typography>
        )}
        {product.salt_composition && (
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.65rem',
              color: 'text.secondary',
              fontWeight: 700,
              mb: 0.5,
              flexShrink: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {product.salt_composition}
          </Typography>
        )}

        {/* Spacer to push bottom content down */}
        <Box sx={{ flex: '1 1 auto', minHeight: 0 }} />

        {/* Price Section */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: { xs: '1.15rem', sm: '1.25rem', md: '1.35rem' },
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}
            >
              ₹{product.price}
            </Typography>
            {product.discount_percent && (
              <Chip
                label={`${Math.round(parseFloat(product.discount_percent) * 100)}% OFF`}
                size="small"
                sx={{ 
                  fontSize: '0.65rem',
                  height: '20px',
                  backgroundColor: '#4caf50',
                  color: '#FFFFFF',
                  fontWeight: 600
                }}
              />
            )}
          </Box>
        </Box>

        {/* Action Button */}
        <Button
          variant="contained"
          fullWidth
          size="small"
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            py: 0.75,
            px: 2,
            borderRadius: '4px',
            boxShadow: 'none',
            backgroundColor: '#1976d2',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: 'none'
            }
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click when button is clicked
            handleCardClick();
          }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
    );
  };

  const ProductSection = ({ title, products, icon, emptyMessage }) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [containerRef, setContainerRef] = useState(null);

    const scrollLeft = () => {
      if (containerRef) {
        const cardWidth = 240; // Approximate card width + spacing
        const newPosition = Math.max(0, scrollPosition - cardWidth);
        setScrollPosition(newPosition);
        containerRef.scrollTo({
          left: newPosition,
          behavior: 'smooth'
        });
      }
    };

    const scrollRight = () => {
      if (containerRef) {
        const cardWidth = 240; // Approximate card width + spacing
        const maxScroll = containerRef.scrollWidth - containerRef.clientWidth;
        const newPosition = Math.min(maxScroll, scrollPosition + cardWidth);
        setScrollPosition(newPosition);
        containerRef.scrollTo({
          left: newPosition,
          behavior: 'smooth'
        });
      }
    };

    const handleScroll = (e) => {
      setScrollPosition(e.target.scrollLeft);
    };

    return (
      <Box sx={{ mb: 4 ,}}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2,
            backgroundColor: 'primary.main',
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 1, sm: 1.25, md: 1.5 },
            borderRadius: 1,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            {icon && <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 0.5, sm: 0.75, md: 1 }, '& svg': { fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } } }}>{icon}</Box>}
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                color: 'white',
                fontFamily: 'Lexend',
                fontWeight: 600,
                textTransform: 'uppercase',
                textAlign: 'left',
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem', lg: '1.1rem' },
              }}
            >
              {title}
            </Typography>
          </Box>
          {products.length > 0 && (
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={scrollLeft}
                disabled={scrollPosition <= 0}
                sx={{
                  minWidth: { xs: 32, sm: 36, md: 40 },
                  height: { xs: 32, sm: 36, md: 40 },
                  borderRadius: '50%',
                  p: 0,
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:disabled': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                  '& svg': {
                    fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem', lg: '1.5rem' },
                  },
                }}
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={scrollRight}
                sx={{
                  minWidth: { xs: 32, sm: 36, md: 40 },
                  height: { xs: 32, sm: 36, md: 40 },
                  borderRadius: '50%',
                  p: 0,
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '& svg': {
                    fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem', lg: '1.5rem' },
                  },
                }}
              >
                <ChevronRight />
              </Button>
            </Box>
          )}
        </Box>
        {products.length > 0 ? (
          <Box sx={{ position: 'relative' }}>
            <Box
              ref={setContainerRef}
              onScroll={handleScroll}
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 2,
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'primary.main',
                  borderRadius: 4,
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                // Hide scrollbar on mobile
                '@media (max-width: 600px)': {
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                },
              }}
            >
              {products.map((product) => (
                <Box
                  key={product.id}
                  sx={{
                    minWidth: { xs: '200px', sm: '220px', md: '240px' },
                    flexShrink: 0,
                  }}
                >
                  <ProductCard product={product} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Hero Video Section */}
        {/* <HeroVideo /> */}
        
        {/* Hero Ad Carousel Section */}
        <HeroAdCarousal />
      <CategoryGrid />
        
        {/* Category-specific Carousels - Discounted products first, then regular products */}
        
        {/* Ayurveda */}
        <DiscountRangeCarousel 
          minDiscount={20} 
          maxDiscount={60} 
          category="Ayurveda"
          title="Up to 60% Off - Ayurveda" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Ayurveda" 
          title="Ayurveda Products" 
          limit={20} 
          minDiscount={1} 
          maxDiscount={20} 
        /> */}
        
        {/* Baby Care */}
        <DiscountRangeCarousel 
          minDiscount={20} 
          maxDiscount={60} 
          category="Baby Care"
          title="Up to 60% Off - Baby Care" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Baby Care" 
          title="Baby Care Products" 
          limit={20} 
        /> */}
        
        {/* Covid Essentials */}
        <DiscountRangeCarousel 
          minDiscount={20} 
          maxDiscount={60} 
          category="Covid Essentials"
          title="Up to 60% Off - Covid Essentials" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Covid Essentials" 
          title="Covid Essentials" 
          limit={20} 
        /> */}
        
        {/* Diabetes Care */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Diabetes Care"
          title="Up to 60% Off - Diabetes Care" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Diabetes Care" 
          title="Diabetes Care Products" 
          limit={20} 
        /> */}
        
        {/* Doctor's Corner */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Doctor's Corner"
          title="Up to 60% Off - Doctor's Corner" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Doctor's Corner" 
          title="Doctor's Corner" 
          limit={20} 
        /> */}
        
        {/* Elderly Care */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Elderly Care"
          title="Up to 60% Off - Elderly Care" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Elderly Care" 
          title="Elderly Care Products" 
          limit={20} 
        /> */}
        
        {/* Hair Care */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Hair Care"
          title="Up to 60% Off - Hair Care" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Hair Care" 
          title="Hair Care Products" 
          limit={20} 
        /> */}
        
        {/* Health Food & Drinks */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Health Food & Drinks"
          title="Up to 60% Off - Health Food & Drinks" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Health Food & Drinks" 
          title="Health Food & Drinks" 
          limit={20} 
        /> */}
        
        {/* Healthcare Devices */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Healthcare Devices"
          title="Up to 60% Off - Healthcare Devices" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Healthcare Devices" 
          title="Healthcare Devices" 
          limit={20} 
        /> */}
        
        {/* Shop by Health Concern */}
        <ShopByHealthConcern />
        
        {/* Homeopathy */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Homeopathy"
          title="Up to 60% Off - Homeopathy" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Homeopathy" 
          title="Homeopathy Products" 
          limit={20} 
        /> */}
        
        {/* Immunity Boosters */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Immunity Boosters"
          title="Up to 60% Off - Immunity Boosters" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Immunity Boosters" 
          title="Immunity Boosters" 
          limit={20} 
        /> */}
        
        {/* Nutritional Drinks */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Nutritional Drinks"
          title="Up to 60% Off - Nutritional Drinks" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Nutritional Drinks" 
          title="Nutritional Drinks" 
          limit={20} 
        /> */}
        
        {/* Oral Care */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Oral Care"
          title="Up to 60% Off - Oral Care" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Oral Care" 
          title="Oral Care Products" 
          limit={20} 
        /> */}
        
        {/* Protein Supplements */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Protein Supplements"
          title="Up to 60% Off - Protein Supplements" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Protein Supplements" 
          title="Protein Supplements" 
          limit={20} 
        /> */}
        
        {/* Sanitizers & Handwash */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Sanitizers & Handwash"
          title="Up to 60% Off - Sanitizers & Handwash" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Sanitizers & Handwash" 
          title="Sanitizers & Handwash" 
          limit={20} 
        /> */}
        
        {/* Sexual Wellness */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Sexual Wellness"
          title="Up to 60% Off - Sexual Wellness" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Sexual Wellness" 
          title="Sexual Wellness Products" 
          limit={20} 
        /> */}
        
        {/* Skin Care */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Skin Care"
          title="Up to 60% Off - Skin Care" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Skin Care" 
          title="Skin Care Products" 
          limit={20} 
        /> */}
        
        {/* Tata 1mg Health Products */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Tata 1mg Health Products"
          title="Up to 60% Off - Tata 1mg Health Products" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Tata 1mg Health Products" 
          title="Tata 1mg Health Products" 
          limit={20} 
        /> */}
        
        {/* Vitamins & Supplements */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Vitamins & Supplements"
          title="Up to 60% Off - Vitamins & Supplements" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Vitamins & Supplements" 
          title="Vitamins & Supplements" 
          limit={20} 
        /> */}
        
        {/* Weight Management */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Weight Management"
          title="Up to 60% Off - Weight Management" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Weight Management" 
          title="Weight Management Products" 
          limit={20} 
        /> */}
        
        {/* Women Care */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          category="Women Care"
          title="Up to 60% Off - Women Care" 
          limit={20} 
        />
        {/* <CategoryCarousel 
          categoryName="Women Care" 
          title="Women Care Products" 
          limit={20} 
        /> */}
        
        {/* Category Grid */}
        
        {/* Product Sections */}
        <ProductSection
          title="Trending Products"
          products={trendingProducts}
          icon={<TrendingUp sx={{ color: 'white' }} />}
          emptyMessage="No trending products available at the moment"
        />

        <ProductSection
          title="Best Deals"
          products={discountProducts}
          icon={<LocalOffer sx={{ color: 'white' }} />}
          emptyMessage="No discount products available at the moment"
        />

        <ProductSection
          title="Best Sellers"
          products={bestSellers}
          icon={<Star sx={{ color: 'white' }} />}
          emptyMessage="No best sellers available at the moment"
        />

        <ProductSection
          title="New Arrivals"
          products={newArrivals}
          icon={<NewReleases sx={{ color: 'white' }} />}
          emptyMessage="No new arrivals available at the moment"
        />

        {/* General Discount Range Carousels - All at the end */}
        <DiscountRangeCarousel 
          minDiscount={0} 
          maxDiscount={60} 
          title="Up to 60% Off - Best Deals" 
          limit={20} 
        />
        
        <DiscountRangeCarousel 
          minDiscount={40} 
          maxDiscount={100} 
          title="40% - 100% Off - Mega Savings" 
          limit={20} 
        />

        {/* Call to Action */}
        {/* <Box sx={{ textAlign: 'center', mt: 6, p: 4, backgroundColor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom>
            Need Help Choosing?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Our healthcare experts are here to help you find the right medicines and products.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => window.location.href = '/consult'}
          >
            Consult Now
          </Button>
        </Box> */}
      </Container>
  );
};

export default HomeView;
